/**
 * Silent session refresh helpers (Cognito refresh via POST /login stage=refresh).
 */

import { extractExpirationFromJWT } from "/assets/js/utils/jwt.js";

/**
 * @param {Date|null} expiresAt
 * @returns {boolean}
 */
export function isTokenExpired(expiresAt) {
  if (!expiresAt) return true;
  return Date.now() >= expiresAt.getTime();
}

/**
 * @param {Date|null} expiresAt
 * @param {number} thresholdMinutes
 * @returns {boolean}
 */
export function shouldRefreshToken(expiresAt, thresholdMinutes = 5) {
  if (!expiresAt) return true;
  const thresholdMs = Math.max(0, Number(thresholdMinutes) || 0) * 60 * 1000;
  return Date.now() >= expiresAt.getTime() - thresholdMs;
}

/**
 * @param {object} params
 * @param {string} params.apiBaseUrl
 * @param {string} params.email
 * @param {string} params.refreshToken
 * @param {typeof fetch} [params.fetchFn]
 */
export async function requestTokenRefresh({
  apiBaseUrl,
  email,
  refreshToken,
  fetchFn = fetch,
}) {
  const endpoint = `${String(apiBaseUrl || "").replace(/\/$/, "")}/login`;
  const response = await fetchFn(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      stage: "refresh",
      refreshToken,
      email: String(email || "")
        .trim()
        .toLowerCase(),
    }),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || payload?.status !== "TOKEN_REFRESHED") {
    const code = payload?.error?.code || `HTTP_${response.status}`;
    const err = new Error(payload?.error?.message || "TOKEN_REFRESH_FAILED");
    err.code = code;
    err.status = response.status;
    throw err;
  }

  const tokens = payload?.data?.tokens || {};
  const accessToken = tokens.accessToken?.value;
  const expiresAt = tokens.accessToken?.expiresAt;
  const idToken = tokens.idToken?.value;
  let idTokenExpiresAt = tokens.idToken?.expiresAt || null;
  if (idToken && !idTokenExpiresAt) {
    idTokenExpiresAt = extractExpirationFromJWT(idToken);
  }

  if (!accessToken || !expiresAt || !idToken) {
    const err = new Error("TOKEN_REFRESH_INCOMPLETE");
    err.code = "TOKEN_REFRESH_INCOMPLETE";
    throw err;
  }

  return { accessToken, expiresAt, idToken, idTokenExpiresAt };
}

/**
 * @param {object} deps
 */
export function createEnsureFreshSession(deps) {
  let inFlight = null;

  async function runEnsure() {
    const idExpires = deps.getIdTokenExpiresAt();
    const accessExpires = deps.getAccessTokenExpiresAt();
    const threshold = deps.getThresholdMinutes();
    const needsRefresh =
      shouldRefreshToken(idExpires, threshold) || shouldRefreshToken(accessExpires, threshold);

    if (!needsRefresh) {
      return { refreshed: false, ok: true };
    }

    const refreshToken = deps.getRefreshToken();
    const email = deps.getEmail();
    if (!refreshToken || !email) {
      deps.onForceLogout("Sessão expirada");
      return { refreshed: false, ok: false, reason: "missing_refresh" };
    }

    const retryKey = deps.getRetryStorageKey();
    const maxAttempts = deps.getRetryMax();
    const requestRefresh = deps.requestRefresh || requestTokenRefresh;

    try {
      const tokens = await requestRefresh({
        apiBaseUrl: deps.getApiBaseUrl(),
        email,
        refreshToken,
        fetchFn: deps.fetchFn,
      });
      deps.storeAuthTokens({
        accessToken: tokens.accessToken,
        expiresAt: tokens.expiresAt,
        idToken: tokens.idToken,
        idTokenExpiresAt: tokens.idTokenExpiresAt,
        refreshToken,
      });
      try {
        localStorage.removeItem(retryKey);
      } catch {
        /* ignore */
      }
      return { refreshed: true, ok: true };
    } catch (error) {
      let attempt = 1;
      try {
        attempt = Number(localStorage.getItem(retryKey) || "0") + 1;
        localStorage.setItem(retryKey, String(attempt));
      } catch {
        attempt = 1;
      }

      if (typeof deps.onRefreshFailure === "function") {
        await deps.onRefreshFailure(attempt, maxAttempts);
      }

      if (attempt >= maxAttempts) {
        deps.onForceLogout("Sessão expirada");
        return { refreshed: false, ok: false, reason: "max_retries", error };
      }

      return { refreshed: false, ok: false, reason: "refresh_failed", attempt, error };
    }
  }

  return async function ensureFreshSession() {
    if (inFlight) return inFlight;
    inFlight = runEnsure().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };
}
